"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Textarea } from "@/app/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Copy, Download, RotateCcw, FileText, Eye } from "lucide-react";
import { toast } from "react-hot-toast";

export default function MarkdownTool() {
  const [inputText, setInputText] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");

  const convertMarkdown = () => {
    if (!inputText.trim()) {
      toast.error("Please enter some markdown text");
      return;
    }

    // Simple markdown to HTML converter
    let html = inputText
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*)\*/gim, '<em>$1</em>')
      // Code blocks
      .replace(/```([\s\S]*?)```/gim, '<pre><code>$1</code></pre>')
      // Inline code
      .replace(/`(.*?)`/gim, '<code>$1</code>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2">$1</a>')
      // Line breaks
      .replace(/\n/gim, '<br>');

    setPreviewHtml(html);
  };

  const copyToClipboard = () => {
    if (!inputText) return;
    navigator.clipboard.writeText(inputText)
      .then(() => toast.success("Markdown copied to clipboard!"))
      .catch(() => toast.error("Failed to copy markdown."));
  };

  const downloadMarkdown = () => {
    if (!inputText) return;
    const blob = new Blob([inputText], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "document.md";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Markdown downloaded!");
  };

  const clearAll = () => {
    setInputText("");
    setPreviewHtml("");
  };

  const loadSample = () => {
    const sample = `# Markdown Sample

This is a **bold** text and this is *italic* text.

## Code Example

Here's some inline \`code\` and a code block:

\`\`\`javascript
function hello() {
  console.log("Hello, World!");
}
\`\`\`

## Links and Lists

- [Visit our website](https://example.com)
- Another item
- Third item

> This is a blockquote

### Table

| Name | Age | City |
|------|-----|------|
| John | 30  | NYC  |
| Jane | 25  | LA   |`;
    
    setInputText(sample);
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="editor" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="editor">Editor</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
          <TabsTrigger value="split">Split View</TabsTrigger>
        </TabsList>

        <TabsContent value="editor" className="space-y-6">
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Markdown Editor
              </CardTitle>
              <CardDescription>
                Write your markdown content here
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Enter your markdown content here..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                className="min-h-[400px] font-mono text-sm"
              />
              
              <div className="flex flex-wrap gap-2">
                <Button onClick={convertMarkdown} className="btn-enhanced hover-lift">
                  Convert to HTML
                </Button>
                <Button onClick={loadSample} variant="outline" className="hover-lift">
                  Load Sample
                </Button>
                <Button onClick={copyToClipboard} variant="outline" className="hover-lift">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
                <Button onClick={downloadMarkdown} variant="outline" className="hover-lift">
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button onClick={clearAll} variant="outline" className="hover-lift">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="space-y-6">
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                HTML Preview
              </CardTitle>
              <CardDescription>
                Preview of your markdown as HTML
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="min-h-[400px] p-4 border rounded-lg bg-muted/20 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: previewHtml || "<p class='text-muted-foreground'>Click 'Convert to HTML' to see preview</p>" }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="split" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Markdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Enter your markdown content here..."
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  className="min-h-[400px] font-mono text-sm"
                />
              </CardContent>
            </Card>

            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="min-h-[400px] p-4 border rounded-lg bg-muted/20 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewHtml || "<p class='text-muted-foreground'>Start typing to see preview</p>" }}
                />
              </CardContent>
            </Card>
          </div>
          
          <div className="flex justify-center">
            <Button onClick={convertMarkdown} className="btn-enhanced hover-lift">
              Update Preview
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>Markdown Guide</CardTitle>
          <CardDescription>
            Common markdown syntax examples
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="font-semibold">Text Formatting</h4>
              <div className="text-sm space-y-1 font-mono">
                <div><strong>**bold**</strong> → <strong>bold</strong></div>
                <div><em>*italic*</em> → <em>italic</em></div>
                <div><code>`code`</code> → <code>code</code></div>
                <div><code>~~strikethrough~~</code> → <del>strikethrough</del></div>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold">Headers</h4>
              <div className="text-sm space-y-1 font-mono">
                <div><code># Header 1</code></div>
                <div><code>## Header 2</code></div>
                <div><code>### Header 3</code></div>
                <div><code>#### Header 4</code></div>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold">Lists</h4>
              <div className="text-sm space-y-1 font-mono">
                <div><code>- Unordered item</code></div>
                <div><code>1. Ordered item</code></div>
                <div><code>[ ] Checkbox</code></div>
                <div><code>[x] Checked</code></div>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold">Links & Images</h4>
              <div className="text-sm space-y-1 font-mono">
                <div><code>[Link text](URL)</code></div>
                <div><code>![Alt text](image.jpg)</code></div>
                <div><code>&gt; Blockquote</code></div>
                <div><code>---</code> (horizontal rule)</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
